/**
 * 干员皮相关 Store
 * 负责干员皮数据的加载、管理和缓存
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useCacheStore } from '../cache'
import { cacheKeys } from '../config'
import { operatorSkinsApi } from '@/https/api/operatorSkins'
import type { OperatorSkin, OperatorSkinCreateRequest, OperatorSkinUpdateRequest } from '@/https/types/operatorSkins'

export const useOperatorSkinsStore = defineStore('operatorSkins', () => {
    const cacheStore = useCacheStore()

    const operatorSkinList = ref<OperatorSkin[]>([])
    const operatorSkinListLoading = ref(false)
    const operatorSkinOperationLoading = ref(false)
    const operatorSkinListError = ref<string | null>(null)

    const hasOperatorSkinList = computed(() => operatorSkinList.value.length > 0)
    const operatorSkinCount = computed(() => operatorSkinList.value.length)

    const sortedOperatorSkinList = computed(() => {
        return [...operatorSkinList.value].sort((a, b) => {
            const orderA = a.operator_skin_order ?? 999999
            const orderB = b.operator_skin_order ?? 999999
            return orderA - orderB
        })
    })

    const operatorSkinOptions = computed(() => {
        return sortedOperatorSkinList.value.map(skin => ({
            label: skin.operator_skin_name,
            value: skin.id
        }))
    })

    const loadOperatorSkinList = async (forceRefresh = false): Promise<OperatorSkin[]> => {
        try {
            operatorSkinListLoading.value = true
            operatorSkinListError.value = null

            const response = await cacheStore.smartCache(
                cacheKeys.config.operatorSkins,
                () => operatorSkinsApi.getAllOperatorSkins({ page: 1, size: 100 }),
                forceRefresh
            )

            let skinsArray: OperatorSkin[] = []
            if (Array.isArray(response)) {
                skinsArray = response
            } else if (response && typeof response === 'object' && 'items' in response) {
                skinsArray = Array.isArray(response.items) ? response.items : []
            }

            operatorSkinList.value = skinsArray
            return operatorSkinList.value
        } catch (error) {
            console.error('❌ [OperatorSkinsStore] 加载干员皮数据失败:', error)
            operatorSkinListError.value = error instanceof Error ? error.message : '加载干员皮数据失败'
            return []
        } finally {
            operatorSkinListLoading.value = false
        }
    }

    const setOperatorSkinList = (items: OperatorSkin[]) => {
        operatorSkinList.value = Array.isArray(items) ? items : []
        operatorSkinListError.value = null
    }

    const refreshOperatorSkinList = async (): Promise<OperatorSkin[]> => {
        return loadOperatorSkinList(true)
    }

    const createOperatorSkin = async (data: OperatorSkinCreateRequest): Promise<OperatorSkin | null> => {
        try {
            operatorSkinOperationLoading.value = true
            operatorSkinListError.value = null
            const created = await operatorSkinsApi.createOperatorSkin(data)
            await refreshOperatorSkinList()
            return created
        } catch (error) {
            console.error('❌ [OperatorSkinsStore] 创建干员皮失败:', error)
            operatorSkinListError.value = error instanceof Error ? error.message : '创建干员皮失败'
            return null
        } finally {
            operatorSkinOperationLoading.value = false
        }
    }

    const updateOperatorSkin = async (id: number, data: OperatorSkinUpdateRequest): Promise<OperatorSkin | null> => {
        try {
            operatorSkinOperationLoading.value = true
            operatorSkinListError.value = null
            const updated = await operatorSkinsApi.updateOperatorSkin(id, data)
            await refreshOperatorSkinList()
            return updated
        } catch (error) {
            console.error('❌ [OperatorSkinsStore] 更新干员皮失败:', error)
            operatorSkinListError.value = error instanceof Error ? error.message : '更新干员皮失败'
            return null
        } finally {
            operatorSkinOperationLoading.value = false
        }
    }

    const deleteOperatorSkin = async (id: number): Promise<boolean> => {
        try {
            operatorSkinOperationLoading.value = true
            operatorSkinListError.value = null
            await operatorSkinsApi.deleteOperatorSkin(id)
            await refreshOperatorSkinList()
            return true
        } catch (error) {
            console.error('❌ [OperatorSkinsStore] 删除干员皮失败:', error)
            operatorSkinListError.value = error instanceof Error ? error.message : '删除干员皮失败'
            return false
        } finally {
            operatorSkinOperationLoading.value = false
        }
    }

    const findOperatorSkinById = (id: number): OperatorSkin | null => {
        return operatorSkinList.value.find(skin => skin.id === id) || null
    }

    const findOperatorSkinByName = (name: string): OperatorSkin | null => {
        return operatorSkinList.value.find(skin => skin.operator_skin_name === name) || null
    }

    const clearOperatorSkinListError = () => {
        operatorSkinListError.value = null
    }

    const initOperatorSkins = async () => {
        if (!hasOperatorSkinList.value) {
            await loadOperatorSkinList()
        }
    }

    return {
        operatorSkinList,
        operatorSkinListLoading,
        operatorSkinOperationLoading,
        operatorSkinListError,

        hasOperatorSkinList,
        operatorSkinCount,
        sortedOperatorSkinList,
        operatorSkinOptions,

        loadOperatorSkinList,
        setOperatorSkinList,
        refreshOperatorSkinList,
        createOperatorSkin,
        updateOperatorSkin,
        deleteOperatorSkin,

        findOperatorSkinById,
        findOperatorSkinByName,
        clearOperatorSkinListError,

        initOperatorSkins
    }
})
